import {
  Component,
  OnInit,
  ViewEncapsulation,
  ElementRef,
  HostBinding,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewContainerRef,
  OnDestroy,
  signal,
  effect,
  Inject,
  PLATFORM_ID
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { Platform } from '@angular/cdk/platform';
import { query, animate, style, group, trigger, transition, sequence, keyframes, AnimationPlayer, AnimationBuilder, AnimationFactory, AnimationEvent } from '@angular/animations';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SearchBoxComponent } from 'app/layout/components/search-box/search-box.component';
import { ToolbarMenuComponent } from 'app/layout/components//toolbar-menu/toolbar-menu.component';
import { LanguagesService, Language } from '@shared/services/languages/languages.service';
import { ToolbarMode } from '@fuse/types';
import { UsersService } from '@shared/services/users/users.service';
import { UserRolesEnum } from '@collections/users/enums/user-roles.enums';
import { LanguageMenuComponent } from '../language-menu/language-menu.component';
import { ToolbarService } from './toolbar.service';
import { getAnimationSteps, IAnimationStepsInfo } from 'app/shared/animations/utilities';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatBadgeModule } from '@angular/material/badge';

import { FlagComponent } from '@shared/components/flag/flag.component';
import { ExpLogoComponent } from '@shared/components/exp-logo/exp-logo.component';
import { MatToolbarModule } from '@angular/material/toolbar';
import { QueryRef } from 'apollo-angular';
import { NewsByDateGQL, NewsByDateResponse } from 'app/shared/services/news/news-by-date.gql.service';
import { Observable, filter, map, tap, catchError, EMPTY, of } from 'rxjs';
import { IPage } from '@collections/common/interfaces';
import { isPlatformBrowser } from '@angular/common';
@UntilDestroy()
@Component({
  selector: 'toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    MatToolbarModule,
    RouterModule,
    MatButtonModule,
    MatIconModule,
    MatBadgeModule,
    FlagComponent,
    ExpLogoComponent,
    LanguageMenuComponent,
    ToolbarMenuComponent,
    SearchBoxComponent,
],
  animations: [
    trigger('searchBoxAnim', [
      transition('searchboxClosed => searchboxOpened', [
        group([
          query(':self', [
            animate('350ms ease-in', style({
              transform: 'translateX(-266px)'
            }))
          ]),
          query('#search-box', [
            animate('350ms ease-in', style({
              visibility: 'visible'
            }))
          ])
        ])
      ]),
      transition('searchboxOpened => searchboxClosed', [
        group([
          query(':self', [
            animate('350ms ease-in', style({
              transform: 'translateX(0px)'
            }))
          ]),
          query('#search-box', [
            animate('350ms ease-in', style({
              visibility: 'hidden'
            }))
          ])
        ])
      ])
    ])
  ],
  providers: [NewsByDateGQL]
})
export class ToolbarComponent implements OnInit, OnDestroy {
  isLoggedIn = signal(false);
  searchMobileOpen = signal(false);  // variabile per il cerca responsive
  searchBoxWrapperCss = signal('');
  hasNewsletterSubscription = false;
  toolbarColor: string;
  newsQuery: QueryRef<NewsByDateResponse>;
  newsNumber: number = -1;
  items$: Observable<any[]>;
  page: IPage<any>;
  totalItemsCount$: Observable<number>;
  lastNewsVisit: string | null = null;

  set toolbarMode(v: ToolbarMode) {
    this._toolbarMode = v;
  }
  get toolbarMode(): ToolbarMode {
    return this._toolbarMode;
  }
  private _toolbarMode: ToolbarMode;

  hiddenToolbar: boolean;
  UserRolesEnum = UserRolesEnum;

  selectedLanguage: Language;

  @HostBinding('class.solid')
  public get solid(): boolean {
    return this.toolbarService.isSolid();
  }

  @HostBinding('class.collapsed')
  public get collapsed(): boolean {
    return this._collapsed();
  }
  
  private _collapsed = signal(false);

  @HostBinding('class.transparent')
  public get transparent(): boolean {
    return this.toolbarService.isTransparent();
  }


  private isAnimating = false;
  private collapseFactory: AnimationFactory;
  private expandFactory: AnimationFactory;
  private currentAnimPlayer: AnimationPlayer;
  private lastAnimPlayer: AnimationPlayer;

  constructor(
    private router: Router,
    public platform: Platform,
    private el: ElementRef,
    private builder: AnimationBuilder,
    private cd: ChangeDetectorRef,
    private languagesService: LanguagesService,
    private usersService: UsersService,
    public toolbarService: ToolbarService,
    private viewContainerRef: ViewContainerRef,
    private newsDateGQL: NewsByDateGQL,
    @Inject(PLATFORM_ID) private platformID: object
  ) {
    
    this.selectedLanguage = this.languagesService.getCurrentLang();

    if (this.platform.isBrowser) {
      this.usersService.onUserChange
      .pipe(
        untilDestroyed(this)
      ).subscribe((user) => {
        this.isLoggedIn.set(!!user);
      });

      effect(() => {
        const collapse = toolbarService.isScrolled();
        if (collapse !== this.collapsed && !this.solid) {
          this.toogleCollapse(collapse);
        } else if(toolbarService.isSolid()) {
          this._collapsed.set(false);
        }
      }, {allowSignalWrites: true})

      
    }
  }
  
  async ngOnInit(): Promise<void> {
    if (isPlatformBrowser(this.platformID)){
      this.lastNewsVisit = this.getLocalStorageItem('lastNewsVisit');
    }
    if (!this.lastNewsVisit){
      this.lastNewsVisit = '2012-12-18T00:00:00'
    }
    this.newsQuery = this.newsDateGQL.watch({
      lang: this.languagesService.getCurrentLangCode(),
      dateFrom: this.lastNewsVisit,
      first: 3,
      newsStatus: 'portal-level-validated'
    });
    this.newsQuery.valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe((res: any) => {
      this.newsNumber = res.data.newsByDate.totalCount || 0;
    });
    const tbs = this.toolbarService;

    tbs.toolbarStateChange$
    .pipe(
      untilDestroyed(this)
    ).subscribe((s) => {
     
      if (this.platform.isBrowser) {
        if (this.currentAnimPlayer) {
          this.currentAnimPlayer.destroy();
          this.currentAnimPlayer = null;
        }

        if (!tbs.isSolid()) {
          this.collapseFactory = this.getAnimationFactory(true, false);
          this.expandFactory = this.getAnimationFactory(false, true);
        }
      }
    });

    if (this.platform.isBrowser) {

      this.hasNewsletterSubscription = !!localStorage.getItem('newsletter_subscribe');
    }
  }


  login(): void {
    this.usersService.openRegister()
/*     const retUrl = this.router.url;
    this.router.navigate(['/login'], { queryParams: { returl: retUrl } }); */
  }

  toogleSearchBox(): void{
    this.searchMobileOpen.update((v) => !v);
  }

  searchBoxAnimDone(ev: AnimationEvent) {
    this.searchBoxWrapperCss.set(ev.fromState==='searchboxClosed' ? 'searchbox-mobile-opened' : '');
  }

  toogleCollapse(collapse: boolean): void {
    this.isAnimating = true;
    const tbs = this.toolbarService;
    tbs.setAnimationState(collapse, 'start');
    const el = this.el.nativeElement;
    const player = collapse ? this.collapseFactory.create(el) : this.expandFactory.create(el);
    player.onDone(() => {
      const _player = this.lastAnimPlayer;
      this.lastAnimPlayer = _player ? (() => { try {_player.destroy(); this.lastAnimPlayer = null; } finally { return player; }})()  : player;
      this._collapsed.set(collapse);
      // this.transparent = tbs.isTransparent();
      this.isAnimating = false;
      this.cd.markForCheck();
      tbs.setAnimationState(collapse, 'end');

    });

    this.currentAnimPlayer = player;
    player.onDestroy(() => {
      this.currentAnimPlayer = null;
    });
    player.play();
  }

  getAnimationFactory(collapse: boolean, reverse: boolean = false): AnimationFactory {
    let isTransparent;
    const stayTransparent = this.toolbarService.toolbarMode() === ToolbarMode.transparent;
    let isLarge: boolean;
    const curve = reverse ? 'ease-out' : 'ease-in';
    const time  = reverse ? '200ms' : '200ms';
    if (collapse) {
      isTransparent = stayTransparent;
      isLarge = this.toolbarService.matchingAliases().includes('md');
    } else {
      isTransparent = true;
      isLarge = true;
    }
    const bg = isTransparent ? 'transparent' : 'rgb(245 245 245 / var(--tw-bg-opacity))';
    const boxShadow = isTransparent ? 'none' : 'var(--tw-drop-shadow)';

    const ranges: IAnimationStepsInfo[] = [
      {from: 120, to: 64, type: 't', key: 'ToolbarH'},
      {from: 0, to: 1, type: 't', key: 'ToolbarCBG'},
      {from: 255, to: 0, type: 't', key: 'ToolbarCOL'},
      {from: 0, to: 100, type: 't', key: 'ToolbarCBGPOS'},
      {from: reverse ? 28 : 0, to: reverse ? 0 : -28, type: 't', key: 'ToolbarCTY'},
      {from: 1, to: 0.675, type: 's', key: 'ToolbarLogoS'},
    ];
    const animSteps = getAnimationSteps(ranges, reverse, 20);
    const anim = this.builder
      .build([
        sequence([
          group([
            query(':self', [
              style({
                display: 'block'
              }),
              animate(`${time} ${curve}`,
              keyframes(animSteps.map((a) =>
                style({
                  height: `${a.get('ToolbarH').inverse}px`,
                  background: stayTransparent ? 'transparent' :
                  `linear-gradient(180deg,  rgb(245 245 245 / 1) 0%, rgb(245 245 245 / ${a.get('ToolbarCBG').inverse})  ${a.get('ToolbarCBGPOS').inverse}%)`,
                })
              )
            ))]),
            query('.ct', [
              animate(`${time} ${curve}`,
              keyframes(animSteps.map((a) =>
                style({
                  transform: `translateY(${a.get('ToolbarCTY').inverse}px)`
                })
              )
            ))]),
            query('.mainCt', [
              style({ backgroundColor: 'transparent' }),
              animate(`${time} ${curve}`,
              keyframes(animSteps.map((a) => {
                const c = a.get('ToolbarCOL').inverse;
                return style({
                  color: stayTransparent ? '#fff' : `rgb(${c} ${c} ${c})`
                });
              }))
            )]),
            query('#tb-logo', [
              animate(`${time} ${curve}`,
                keyframes(animSteps.map((a) =>
                  style({
                    transform: `scale(${a.get('ToolbarLogoS').step})`,
                    transformOrigin: 'center left'
                  })
                )
            ))])
        ]),
        group([
            query('.ct', [
              style({
                transform: 'translateY(0px)'
              }),
            ]),
            query('.mainCt', [
              style({
                backgroundColor: bg,
                boxShadow: boxShadow
              }),
            ])
          ])
      ])

    ]);

    return anim;
  }

  ngOnDestroy(): void {
    if (this.lastAnimPlayer) {
      this.lastAnimPlayer.destroy();
    }
  }

  showNewsletterBanner() {
    this.toolbarService.showNewsletterSubscribeDialog(this.viewContainerRef);
  }
  getLocalStorageItem(key: string): string | null {
    return localStorage.getItem(key);
  }

  getBadgeValue() {
    if (this.newsNumber > 99) return '99+';
    if (this.newsNumber > 0 && this.newsNumber <= 99) return (this.newsNumber);
    return '';
  }
}
